import { FC, useEffect, useCallback, useState } from 'react';

import { Link, router, usePage } from '@inertiajs/react';
import {
    Anchor,
    BarChart3,
    Home,
    LogOut,
    MessageSquareText,
    Monitor,
    Moon,
    Package,
    Plus,
    Sun,
    ShoppingBag,
    ShoppingCart,
    Settings,
    Store,
    User,
    X,
    CircleDollarSign,
    Heart,
} from 'lucide-react';

import { CUSTOMER_LINKS, SOCIAL_LINKS } from './siteNavigation';
import { Routes } from '../../models/routes';
import { CookieConsentService } from '../../services/CookieConsentService';
import { Appearance, ThemeService } from '../../services/ThemeService';
import { CurrentUser, Universe } from '../../types';
import { cn, formatBalanceCents } from '../../utils';
import { Button } from '../ui/Button';
import { Separator } from '../ui/Separator';

interface NavItem {
    label: string;
    href: string;
    icon: FC<{ className?: string }>;
    match: string;
    badge?: number;
    detail?: string;
}

interface AccountNavCounts {
    offersCount?: number;
    salesCount?: number;
    purchasesCount?: number;
    reviewsCount?: number;
    wishlistCount?: number;
    balanceCents?: number;
}

const getAccountNavItems = (
    currentUser: CurrentUser,
    accountNav?: AccountNavCounts,
    newNotificationPresent?: boolean,
): NavItem[] => [
    {
        label: 'Профіль',
        href: `${Routes.User}/${currentUser.nickname}`,
        icon: User,
        match: `/user/${currentUser.nickname}`,
    },
    {
        label: 'Баланс',
        href: Routes.Balance,
        icon: CircleDollarSign,
        match: '/profile/balance',
        detail:
            accountNav?.balanceCents == null
                ? undefined
                : formatBalanceCents(accountNav.balanceCents),
    },
    {
        label: 'Пропозиції',
        href: Routes.Offers,
        icon: Package,
        match: '/profile/offers',
        badge: accountNav?.offersCount,
    },
    {
        label: 'Аналіз цін',
        href: Routes.OffersAnalytics,
        icon: BarChart3,
        match: Routes.OffersAnalytics,
    },
    {
        label: 'Продажі',
        href: Routes.Sales,
        icon: ShoppingBag,
        match: '/profile/sales',
        badge: newNotificationPresent ? undefined : accountNav?.salesCount,
    },
    {
        label: 'Покупки',
        href: Routes.Purchases,
        icon: ShoppingCart,
        match: '/profile/purchases',
        badge: accountNav?.purchasesCount,
    },
    {
        label: 'Список бажаного',
        href: Routes.Wishlist,
        icon: Heart,
        match: Routes.Wishlist,
        badge: accountNav?.wishlistCount,
    },
    {
        label: 'Відгуки',
        href: Routes.Reviews,
        icon: MessageSquareText,
        match: '/profile/reviews',
        badge: accountNav?.reviewsCount,
    },
    {
        label: 'Редагувати профіль',
        href: Routes.ProfileEdit,
        icon: Settings,
        match: '/profile/edit',
    },
];

interface Props {
    open: boolean;
    onClose: () => void;
}

type PageProps = {
    accountNav?: AccountNavCounts;
    newNotificationPresent: boolean;
    currentUser: CurrentUser | null;
    universes: Universe[];
};

const appearanceOptions: Array<{
    value: Appearance;
    label: string;
    icon: FC<{ className?: string }>;
}> = [
    { value: 'light', label: 'Світла', icon: Sun },
    { value: 'dark', label: 'Темна', icon: Moon },
    { value: 'system', label: 'Системна', icon: Monitor },
];

const NavLink: FC<{
    href: string;
    icon: FC<{ className?: string }>;
    label: string;
    active?: boolean;
    badge?: number;
    showDot?: boolean;
    detail?: string;
}> = ({ href, icon: Icon, label, active, badge, showDot, detail }) => (
    <Link
        href={href}
        className={cn(
            'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
            'hover:bg-accent hover:text-accent-foreground',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
            active && 'bg-primary/10 text-primary hover:bg-primary/15',
        )}
        aria-current={active ? 'page' : undefined}
    >
        <div className="relative">
            <Icon
                className={cn(
                    'h-4 w-4 shrink-0',
                    active
                        ? 'text-primary'
                        : 'text-muted-foreground group-hover:text-accent-foreground',
                )}
            />
            {showDot && (
                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 animate-pulse rounded-full bg-yellow-400" />
            )}
        </div>
        <span className="flex-1">{label}</span>
        {detail && (
            <span className="text-xs text-muted-foreground">{detail}</span>
        )}
        {badge != null && badge > 0 && (
            <span
                className={cn(
                    'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold',
                    active
                        ? 'bg-primary/20 text-primary'
                        : 'bg-muted text-muted-foreground',
                )}
            >
                {badge}
            </span>
        )}
    </Link>
);

export const MobileAccountDrawer: FC<Props> = ({ open, onClose }) => {
    const { url } = usePage();
    const { accountNav, newNotificationPresent, currentUser, universes } =
        usePage<PageProps>().props;
    const [appearance, setAppearance] = useState<Appearance>('system');
    const accountNavItems = currentUser
        ? getAccountNavItems(currentUser, accountNav, newNotificationPresent)
        : [];
    const isActive = (match: string) => url.startsWith(match);
    const activeAccountMatch = accountNavItems.reduce<string | null>(
        (longestMatch, item) => {
            if (!isActive(item.match)) return longestMatch;
            if (!longestMatch || item.match.length > longestMatch.length) {
                return item.match;
            }

            return longestMatch;
        },
        null,
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        },
        [onClose],
    );

    // Close drawer after Inertia finishes navigating (not on click)
    useEffect(() => {
        if (!open) return;

        const removeFinishListener = router.on('finish', () => {
            onClose();
        });

        return () => {
            removeFinishListener();
        };
    }, [open, onClose]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', handleKeyDown);
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, handleKeyDown]);

    useEffect(() => {
        const syncAppearance = () => {
            setAppearance(ThemeService.getAppearance());
        };

        syncAppearance();
        window.addEventListener('theme-change', syncAppearance);

        return () => {
            window.removeEventListener('theme-change', syncAppearance);
        };
    }, []);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50"
            role="dialog"
            aria-modal="true"
            aria-label="Меню"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Panel */}
            <div className="animate-in slide-in-from-left absolute inset-0 bg-background duration-300">
                {/* Header */}
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <span className="text-sm font-semibold">Меню</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                        aria-label="Закрити меню"
                        autoFocus
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex h-[calc(100%-57px)] flex-col overflow-y-auto">
                    {/* Quick links */}
                    <div className="px-4 pt-4 pb-2">
                        <NavLink
                            href={Routes.Home}
                            icon={Home}
                            label="Головна"
                            active={url === Routes.Home}
                        />
                        <NavLink
                            href={Routes.Sellers}
                            icon={Store}
                            label="Продавці"
                            active={url.startsWith(Routes.Sellers)}
                        />
                    </div>

                    {/* Account section — only for logged in users */}
                    {currentUser && (
                        <>
                            <Separator className="my-2" />

                            <div className="px-4 py-2">
                                {/* Create offer CTA */}
                                <div className="mb-2 px-1">
                                    <Button
                                        asChild
                                        className="w-full"
                                        size="sm"
                                    >
                                        <Link href={Routes.NewOffer}>
                                            <Plus className="mr-2 h-4 w-4" />
                                            Створити пропозицію
                                        </Link>
                                    </Button>
                                </div>

                                {/* Account nav items */}
                                <nav
                                    className="space-y-1 px-1"
                                    aria-label="Кабінет"
                                >
                                    {accountNavItems.map((item) => (
                                        <NavLink
                                            key={item.href}
                                            href={item.href}
                                            icon={item.icon}
                                            label={item.label}
                                            active={
                                                item.match ===
                                                activeAccountMatch
                                            }
                                            badge={item.badge}
                                            showDot={
                                                item.match ===
                                                    '/profile/sales' &&
                                                newNotificationPresent &&
                                                !isActive(item.match)
                                            }
                                            detail={item.detail}
                                        />
                                    ))}
                                </nav>
                            </div>
                        </>
                    )}

                    {/* Universes / TCG section */}
                    {universes?.length > 0 && (
                        <>
                            <Separator className="my-2" />

                            <div className="px-4 py-2">
                                <h2 className="mb-2 px-3 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                                    TCG
                                </h2>
                                <div className="space-y-1">
                                    {universes.map((universe) => (
                                        <NavLink
                                            key={universe.id}
                                            href={universe.url_path}
                                            icon={Anchor}
                                            label={universe.name}
                                            active={url.startsWith(
                                                universe.url_path,
                                            )}
                                        />
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    <Separator className="my-2" />

                    <div className="px-5 py-3">
                        <h2 className="mb-2 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                            Клієнтам
                        </h2>
                        <nav
                            className="grid grid-cols-2 gap-x-4 gap-y-1"
                            aria-label="Інформація"
                        >
                            {CUSTOMER_LINKS.map((link) => (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    className="rounded-lg px-2 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                                >
                                    {link.label}
                                </Link>
                            ))}
                            <button
                                type="button"
                                className="rounded-lg px-2 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                                onClick={() => {
                                    onClose();
                                    CookieConsentService.requestReview();
                                }}
                            >
                                Cookies
                            </button>
                        </nav>
                    </div>

                    <Separator className="my-2" />

                    <div className="px-5 py-3">
                        <h2 className="mb-2 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                            Соцмережі
                        </h2>
                        <div className="flex flex-wrap gap-2">
                            {SOCIAL_LINKS.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    target="_blank"
                                    rel="nofollow noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                                    onClick={onClose}
                                >
                                    <link.icon className="h-4 w-4" />
                                    {link.label}
                                </a>
                            ))}
                        </div>
                    </div>

                    <Separator className="my-2" />

                    <div className="px-5 py-3 lg:hidden">
                        <div className="mb-3 px-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                            Тема
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            {appearanceOptions.map((option) => {
                                const Icon = option.icon;
                                const active = appearance === option.value;

                                return (
                                    <Button
                                        key={option.value}
                                        type="button"
                                        variant={active ? 'default' : 'outline'}
                                        size="sm"
                                        className={cn(
                                            'h-auto flex-col gap-1.5 rounded-xl px-2 py-2.5 text-xs',
                                            !active &&
                                                'bg-transparent text-muted-foreground',
                                        )}
                                        onClick={() =>
                                            ThemeService.applyAppearance(
                                                option.value,
                                            )
                                        }
                                    >
                                        <Icon className="h-4 w-4" />
                                        <span>{option.label}</span>
                                    </Button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Logout — only for logged in users */}
                    {currentUser && (
                        <div className="mt-auto border-t px-5 pt-3 pb-4">
                            <Link
                                href={Routes.SignOut}
                                method="delete"
                                as="button"
                                className={cn(
                                    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                                    'text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300',
                                    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                                )}
                                onClick={onClose}
                            >
                                <LogOut className="h-4 w-4 shrink-0" />
                                Вийти
                            </Link>
                        </div>
                    )}

                    {/* Sign in — for non-logged in users */}
                    {!currentUser && (
                        <div className="mt-auto border-t px-5 pt-3 pb-4">
                            <div className="space-y-2">
                                <Button asChild className="w-full" size="sm">
                                    <Link href={Routes.SignIn}>Увійти</Link>
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="w-full"
                                    onClick={onClose}
                                >
                                    Сховати
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
